import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";
import { NotFoundError } from "@shared/utils/not-found-error";
import { getSeoConfig } from "@shared/utils/seo/seo-helper";

import { getMapPoints } from "@/_pages/map/api";
import type { MapPageType } from "@/_pages/map/model";

import { mapDtoToAccommodationsPoints } from "../model";
import type { AccommodationsPointType } from "../model/types";

type AccommodationsPointPageResponse = {
  point: AccommodationsPointType;
  mapData: Pick<
    MapPageType,
    "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
  >;
  seo: ReturnType<(typeof getSeoConfig)["accommodationsPoint"]>;
};

export const getAccommodationsPointPage = async (
  slug: string,
): Promise<AccommodationsPointPageResponse> => {
  const searchParams = {
    filters: {
      uid: {
        $eq: slug,
      },
    },
    populate: {
      photos: {
        populate: "*",
      },
      workTimeList: {
        populate: "*",
      },
      seo: {
        populate: {
          ogImage: {
            populate: "*",
          },
        },
      },
      content: {
        on: {
          "article.text-block": {
            populate: "*",
          },
          "article.quote-with-author": {
            populate: "*",
          },
          "article.table": {
            populate: {
              tabletRows: {
                populate: "*",
              },
              columnTitle: {
                populate: "*",
              },
              columnSubtitle: {
                populate: "*",
              },
            },
          },
          "article.gallery": {
            populate: "*",
          },
          "article.video": {
            populate: "*",
          },
          "article.list-with-icon": {
            populate: {
              icon: {
                populate: "*",
              },
              items: {
                populate: "*",
              },
            },
          },
          "article.conditions-table": {
            populate: {
              rows: {
                populate: "*",
              },
            },
          },
          "article.accordions-text": {
            populate: "*",
          },
        },
      },
      other_accommodations: {
        populate: {
          photos: {
            populate: "*",
          },
          workTimeList: {
            populate: "*",
          },
          accommodations_type: {
            populate: "*",
          },
          accommodations_badge_types: {
            populate: "*",
          },
        },
      },
      accommodations_type: {
        populate: "*",
      },
      accommodations_badge_types: {
        populate: "*",
      },
    },
  };

  const accommodationsPoints = strapiClient.collection("accommodations-points");

  const page = await accommodationsPoints.find({ ...searchParams });

  const points = mapDtoToAccommodationsPoints(page).data;
  const point = points[0];

  if (!point) {
    throw new NotFoundError(
      `Accommodations point with slug "${slug}" not found`,
    );
  }

  const mapData = await getMapPoints();
  const seo = getSeoConfig.accommodationsPoint(point);

  return rebuild({
    point: {
      ...point,
      seo,
    } as AccommodationsPointType,
    mapData,
    seo,
  }) as AccommodationsPointPageResponse;
};
